/* eslint-disable */
import React, { Suspense, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom'
import { connect } from 'react-redux'
import { getCurrentUser as getCurrentUserRedux } from './redux'
import TopLoader from './components/commons/TopLoader'
import TuringUserValidation from './components/forms/TusingUserValidation.jsx'

// 💤 Lazy-load all route components
const NavBar = React.lazy(() => import('./components/commons/NavBar'))
const FloatingButton = React.lazy(() =>
    import('./components/commons/FloatingButton')
)
const Footer = React.lazy(() => import('./components/commons/Footer'))
const LandingPage = React.lazy(() => import('./components/LandingPage'))
const Login = React.lazy(() => import('./components/Login'))
const Register = React.lazy(() => import('./components/Register'))
const Careers = React.lazy(() => import('./components/Careers'))
const GradeAnalyzer = React.lazy(() => import('./components/GradeAnalyzer'))
const VerifyUser = React.lazy(() => import('./components/VerifyUser'))
const GenerateResetPasswordEmail = React.lazy(() =>
    import('./components/GenerateResetPasswordEmail')
)
const ResetPassword = React.lazy(() => import('./components/ResetPassword'))
const ContactUs = React.lazy(() => import('./components/ContactUs'))
const AdminPanel = React.lazy(() => import('./components/AdminPanel.jsx'))
const MyNotesPage = React.lazy(() => import('./components/notes/MyNotesPage'))
const NotePage = React.lazy(() => import('./components/notes/NotePage'))
const PublicNotePage = React.lazy(() =>
    import('./components/notes/PublicNotePage')
)

// Profile
const Profile = React.lazy(() => import('./v3/pages/ProfilePage.jsx'))
const ProfilePageOthers = React.lazy(() =>
    import('./v3/pages/ProfilePageOthers.jsx')
)

// Quiz + Courses
const Courses = React.lazy(() => import('./components/courses-new/Courses'))
const Course = React.lazy(() => import('./components/courses-new/Course.jsx'))
const Assignment = React.lazy(() =>
    import(
        './components/courses-new/tabs/assignments/Assignment/Assignment.jsx'
    )
)
const QuizPageV2 = React.lazy(() => import('./components/quiz-new/Quiz.jsx'))
const QuizJoinPage = React.lazy(() =>
    import('./components/quiz-new/QuizJoinPage.jsx')
)
const QuizPageV3 = React.lazy(() => import('./v3/pages/QuizPage.jsx'))
const QuizDemoPage = React.lazy(() => import('./v3/pages/QuizDemoPage.jsx'))
const CreateQuiz = React.lazy(() => import('./components/quiz-new/CreateQuiz'))
const QuizCategories = React.lazy(() =>
    import('./components/quiz/QuizCategories')
)
const UploadQuizQuestion = React.lazy(() =>
    import('./components/quiz/uploadQuizQuestions.jsx')
)
const QuizInstructorPage = React.lazy(() =>
    import('./v3/pages/QuizInstructorPage.jsx')
)
const QuestionCreatorDashboard = React.lazy(() =>
    import('./v3/components/questionmanagement/QuestionCreatorDashboard.jsx')
)

// Canvas
const CanvasRequestAccess = React.lazy(() =>
    import('./components/canvas/CanvasRequestAccess')
)
const CourseCards = React.lazy(() => import('./components/canvas/CoursesInfo'))
const CourseDetails = React.lazy(() =>
    import('./components/canvas/CourseDetails')
)
const Home = React.lazy(() => import('./components/canvas/CanvasCoursHome'))
const Modules = React.lazy(() =>
    import('./components/canvas/CanvasCourseModules')
)
const CanvasQuiz = React.lazy(() => import('./components/canvas/CanvasQuiz'))
const CanvasAssignments = React.lazy(() =>
    import('./components/canvas/canvasCourseAssignments.jsx')
)
const CanvasCreateQuiz = React.lazy(() =>
    import('./components/canvas/CanvasCreateQuiz')
)

// Misc
const MyAccountForm = React.lazy(() => import('./components/MyAccountForm'))
const CoursesList = React.lazy(() => import('./components/courses/CoursesList'))
const CoursesHome = React.lazy(() => import('./components/courses/CoursesHome'))

function Routes({ userData, getCurrentUser }) {
    const [hideNavAndTA, setHideNavAndTA] = useState(false)

    useEffect(() => {
        if (!userData.user || userData.user.id === undefined) {
            getCurrentUser()
        }
    }, [userData.user, getCurrentUser])

    useEffect(() => {
        const handleToggle = (e) => {
            setHideNavAndTA(!!e.detail?.hide)
        }
        window.addEventListener('toggle-quiz-ui', handleToggle)
        return () => {
            window.removeEventListener('toggle-quiz-ui', handleToggle)
        }
    }, [])

    const isProd = process.env.REACT_APP_PROD === 'true'
    const isInviteRoute = window.location.pathname === '/quiz/join'
    const [isValidated, setIsValidated] = useState(() =>
        isProd || isInviteRoute ? true : localStorage.getItem('validated')
    )

    const userRole = userData?.user?.role || 'Student'

    return (
        <>
            {userData.loading && (
                <TopLoader message={userData.loadingMessage} />
            )}
            <BrowserRouter>
                <Suspense fallback={<TopLoader message="Loading..." />}>
                    {isValidated && !hideNavAndTA && <NavBar />}

                    <Switch>
                        {isValidated || userData === undefined ? (
                            <>
                                <Route exact path="/" component={LandingPage} />
                                <Route
                                    exact
                                    path="/contact-us"
                                    component={ContactUs}
                                />
                                <Route
                                    exact
                                    path="/display-account/:email"
                                    component={ProfilePageOthers}
                                />
                                <Route
                                    exact
                                    path="/careers"
                                    component={Careers}
                                />
                                <Route
                                    exact
                                    path="/grade-analyzer"
                                    component={GradeAnalyzer}
                                />
                                <Route exact path="/login" component={Login} />
                                <Route
                                    exact
                                    path="/register"
                                    component={Register}
                                />
                                <Route
                                    exact
                                    path="/verify-user"
                                    component={VerifyUser}
                                />
                                <Route
                                    exact
                                    path="/admin-panel"
                                    component={AdminPanel}
                                />
                                <Route
                                    exact
                                    path="/question-creator-dashboard"
                                    component={QuestionCreatorDashboard}
                                />
                                <Route
                                    exact
                                    path="/reset-password/generate"
                                    component={GenerateResetPasswordEmail}
                                />
                                <Route
                                    exact
                                    path="/reset-password"
                                    component={ResetPassword}
                                />
                                <Route
                                    exact
                                    path="/courses"
                                    component={Courses}
                                />
                                <Route
                                    exact
                                    path="/course/:id"
                                    component={Course}
                                />
                                <Route
                                    exact
                                    path="/quiz/categories"
                                    component={QuizCategories}
                                />
                                <Route
                                    exact
                                    path="/quiz/create-quiz"
                                    component={CreateQuiz}
                                />
                                <Route
                                    exact
                                    path="/quiz/manage/:courseId"
                                    component={CreateQuiz}
                                />
                                <Route
                                    exact
                                    path="/manageQuestions"
                                    component={UploadQuizQuestion}
                                />
                                <Route
                                    exact
                                    path="/quiz/v2/:quiz_id"
                                    component={QuizPageV2}
                                />
                                <Route
                                    exact
                                    path="/quiz/v3/:quiz_id"
                                    component={QuizPageV3}
                                />
                                <Route
                                    exact
                                    path="/quiz/iesmarvo/demo"
                                    component={QuizDemoPage}
                                />

                                <Route
                                    exact
                                    path="/quiz/instructor/:course_id/:quiz_id"
                                    component={QuizInstructorPage}
                                />
                                <Route
                                    exact
                                    path="/course/:id/assignment/:assignment_id"
                                    component={Assignment}
                                />
                                <Route
                                    exact
                                    path="/canvas"
                                    component={CanvasRequestAccess}
                                />
                                <Route
                                    exact
                                    path="/myaccount"
                                    component={Profile}
                                />
                                <Route
                                    exact
                                    path="/notes"
                                    component={MyNotesPage}
                                />
                                <Route
                                    exact
                                    path="/note/:id"
                                    component={NotePage}
                                />
                                <Route
                                    exact
                                    path="/course/:courseId/public-note/:id"
                                    render={(props) => (
                                        <PublicNotePage
                                            {...props}
                                            role={userRole}
                                        />
                                    )}
                                />
                                <Route
                                    exact
                                    path="/upgrade-form/:user"
                                    component={MyAccountForm}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas"
                                    component={CourseCards}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas/:course_id"
                                    component={CourseDetails}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas/:course_id/home"
                                    component={Home}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas/:course_id/modules"
                                    component={Modules}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas/:course_id/quiz"
                                    component={CanvasQuiz}
                                />
                                <Route
                                    exact
                                    path="/courses/canvas/:course_id/assignments"
                                    component={CanvasAssignments}
                                />
                                <Route
                                    exact
                                    path="/v1/courses"
                                    component={CoursesList}
                                />
                                <Route
                                    exact
                                    path="/v1/course/home/:course_id"
                                    component={CoursesHome}
                                />
                                <Route
                                    exact
                                    path="/v1/courses/canvas/:course_id/quiz/edit"
                                    component={CanvasCreateQuiz}
                                />
                                <Route
                                    exact
                                    path="/v1/course/:course_id"
                                    component={GradeAnalyzer}
                                />
                                <Route
                                    exact
                                    path="/quiz/join"
                                    component={QuizJoinPage}
                                />
                            </>
                        ) : (
                            <Route
                                path="/validate"
                                render={() => (
                                    <TuringUserValidation
                                        onValidate={() => setIsValidated(true)}
                                    />
                                )}
                            />
                        )}

                        <Route
                            render={() => (
                                <Redirect
                                    to={isValidated ? '/' : '/validate'}
                                />
                            )}
                        />
                    </Switch>

                    {isValidated && !hideNavAndTA && (
                        <>
                            <FloatingButton />
                            <Footer />
                        </>
                    )}
                </Suspense>
            </BrowserRouter>
        </>
    )
}

Routes.propTypes = {
    userData: PropTypes.shape({
        loading: PropTypes.bool,
        loadingMessage: PropTypes.string,
        user: PropTypes.shape({
            id: PropTypes.string,
        }),
    }).isRequired,
    getCurrentUser: PropTypes.func.isRequired,
}

const mapStateToProps = (state) => ({
    userData: state.userData,
})

const mapDispatchToProps = (dispatch) => ({
    getCurrentUser: () => dispatch(getCurrentUserRedux()),
})

export default connect(mapStateToProps, mapDispatchToProps)(Routes)
